import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Maximize2,
  ScanSearch,
} from "lucide-react";
import { useState } from "react";
import { isImageUrl, useImagePreview } from "@/context/ImagePreview";

interface Props {
  images: string[];
}

const getFileName = (url: string) =>
  decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "");

const getExt = (url: string) =>
  url.split(".").pop()?.split("?")[0]?.toUpperCase() ?? "";

// ─── Shimmer skeleton ────────────────────────────────────────────────────────
const Shimmer = () => (
  <div className="absolute inset-0 rounded-sm overflow-hidden">
    <div
      className="w-full h-full animate-shimmer"
      style={{
        background:
          "linear-gradient(90deg, var(--color-muted) 25%, var(--color-border) 50%, var(--color-muted) 75%)",
        backgroundSize: "200% 100%",
      }}
    />
  </div>
);

// ─── Non-image document card ─────────────────────────────────────────────────
const DocCard = ({
  url,
  t,
}: {
  url: string;
  t: (key: string, opts?: any) => string;
}) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col items-center justify-center gap-4 w-full h-full"
    aria-label={t("shared_ui.open_named_file", { name: getFileName(url) })}
  >
    {/* Subtle dot-grid background container */}
    <div
      className="w-20 h-20 rounded-2xl flex items-center justify-center relative overflow-hidden
                 bg-primary/10 group-hover:bg-primary/5 transition-colors duration-200"
      style={{
        backgroundImage:
          "radial-gradient(circle, var(--color-primary) 1px, transparent 1px)",
        backgroundSize: "10px 10px",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/5 transition-colors duration-200" />
      <FileText className="relative w-9 h-9 text-primary" />
    </div>

    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[11px] font-bold tracking-widest text-primary bg-primary/10 px-2.5 py-0.5 rounded-full uppercase">
        {getExt(url)}
      </span>
      <span className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary transition-colors duration-150">
        {t("shared_ui.open_file")}
        <ExternalLink className="w-3 h-3" />
      </span>
    </div>
  </a>
);

// ─── Thumbnail cell ───────────────────────────────────────────────────────────
const Thumbnail = ({
  url,
  active,
  onClick,
  index,
  t,
}: {
  url: string;
  active: boolean;
  onClick: () => void;
  index: number;
  t: (key: string, opts?: any) => string;
}) => (
  <button
    onClick={onClick}
    aria-label={t("shared_ui.preview_document_n", { num: index + 1 })}
    aria-pressed={active}
    className={clsx(
      "relative w-[72px] h-[54px] shrink-0 rounded-sm overflow-hidden border-2 transition-all duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50",
      active
        ? "border-primary scale-[0.96] shadow-md"
        : "border-transparent hover:border-border hover:scale-[0.96]",
    )}
  >
    {isImageUrl(url) ? (
      <img src={url} alt="" className="w-full h-full object-cover" />
    ) : (
      <div className="w-full h-full flex flex-col items-center justify-center gap-0.5 bg-primary/10">
        <FileText className="w-4 h-4 text-primary" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-primary/70">
          {getExt(url)}
        </span>
      </div>
    )}

    {/* Active tint overlay */}
    {active && (
      <div className="absolute inset-0 bg-primary/10 pointer-events-none" />
    )}
  </button>
);

// ─── Main component ───────────────────────────────────────────────────────────
const DocumentQuickPreview = ({ images }: Props) => {
  const { t } = useTranslation("common");
  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const openPreview = useImagePreview();

  const total = images?.length ?? 0;
  const hasMultiple = total > 1;

  const navigate = (dir: 1 | -1) => {
    setIsLoading(true);
    setCurrent((i) => (i + dir + total) % total);
  };

  const select = (index: number) => {
    if (index !== current) {
      setIsLoading(true);
      setCurrent(index);
    }
  };

  const currentUrl = images[current] ?? "";
  const isImage = isImageUrl(currentUrl);
  const fileName = getFileName(currentUrl);

  return (
    <div className="bg-muted w-full lg:w-[500px] me-[-24px] flex flex-col select-none">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <ScanSearch className="w-4 h-4 text-muted-foreground" />
          <h4 className="font-semibold text-sm text-foreground">
            {t("my-space:ui.quick_preview")}
          </h4>
        </div>
        <div className="flex items-center gap-2">
          {hasMultiple && (
            <span className="text-[11px] font-semibold text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full tabular-nums">
              {current + 1}&thinsp;/&thinsp;{total}
            </span>
          )}
          {isImage && (
            <button
              onClick={() => openPreview(images, undefined)}
              aria-label={t("shared_ui.full_screen_preview")}
              className="w-7 h-7 flex items-center justify-center rounded-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-150"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-4 flex flex-col gap-3">
        {/* Main preview */}
        <div className="relative rounded-sm border border-border bg-white overflow-hidden">
          {/* Fixed 4:3 aspect ratio box */}
          <div className="w-full" style={{ paddingBottom: "75%" }}>
            <div className="absolute inset-0 flex items-center justify-center p-5">
              {isImage ? (
                <>
                  {isLoading && <Shimmer />}
                  <img
                    key={currentUrl}
                    src={currentUrl}
                    alt={t("shared_ui.document_preview")}
                    onLoad={() => setIsLoading(false)}
                    className={clsx(
                      "max-h-full max-w-full object-contain rounded-xxs transition-opacity duration-300",
                      isLoading ? "opacity-0" : "opacity-100",
                    )}
                  />
                </>
              ) : (
                <DocCard url={currentUrl} t={t} />
              )}
            </div>
          </div>

          {/* Nav arrows */}
          {hasMultiple && (
            <>
              <button
                onClick={() => navigate(-1)}
                aria-label={t("shared_ui.previous_document")}
                className="absolute start-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 backdrop-blur-xs border border-border shadow-xs
                           flex items-center justify-center text-muted-foreground
                           hover:text-primary hover:border-primary hover:bg-white transition-all duration-150"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate(1)}
                aria-label={t("shared_ui.next_document")}
                className="absolute end-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 backdrop-blur-xs border border-border shadow-xs
                           flex items-center justify-center text-muted-foreground
                           hover:text-primary hover:border-primary hover:bg-white transition-all duration-150"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Filename chip */}
        {fileName && (
          <p
            className="text-[11px] text-muted-foreground text-center truncate px-2"
            title={fileName}
          >
            {fileName}
          </p>
        )}

        {/* Thumbnail strip — always flex row, scrolls when many */}
        {hasMultiple && (
          <div className="flex gap-2 overflow-x-auto pb-0.5">
            {images.map((url, index) => (
              <Thumbnail
                key={index}
                url={url}
                index={index}
                active={current === index}
                onClick={() => select(index)}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentQuickPreview;
